import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      // Cookie is set by the endpoint — refresh context and route to dashboard.
      try { await refresh?.(); } catch { /* non-fatal */ }
      toast.success("Password updated. You're signed in.");
      nav("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      <div className="hidden lg:flex lg:col-span-2 bg-[--sky] text-[--ink] p-10 flex-col justify-between border-r border-[--line]">
        <Link to="/" data-testid="brand-link" className="flex items-center">
          <Logo className="h-9" />
        </Link>
        <div>
          <div className="overline text-[--accent] mb-4">PASSWORD RESET</div>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-[0.95]">
            One-time link.
            <br />
            New password.
          </h2>
          <p className="mt-4 text-[--ink]/70 max-w-sm leading-relaxed">
            Pick something memorable. You'll be signed in automatically once it's saved.
          </p>
        </div>
        <div className="font-mono text-xs text-[--muted]">Flowscribe · Speech customized.</div>
      </div>

      <div className="lg:col-span-3 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center mb-10">
            <Logo className="h-8" />
          </Link>
          <div className="overline text-[--muted] mb-4">RESET PASSWORD</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-8" data-testid="reset-password-title">
            Choose a new password.
          </h1>

          {!token ? (
            <div className="border border-amber-500 bg-amber-50 p-4 text-xs text-amber-900 font-mono leading-relaxed" data-testid="reset-missing-token">
              This reset link is incomplete. Please request a fresh one from the sign-in page.
              <div className="mt-3">
                <Link to="/login" className="underline font-bold">Back to sign in →</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <label className="overline block mb-2">New password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                  placeholder="At least 6 characters"
                />
              </div>
              <div>
                <label className="overline block mb-2">Confirm password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                  placeholder="Type it again"
                />
              </div>
              {err && (
                <div className="border border-red-500 bg-red-50 p-3 text-xs text-red-800 font-mono" data-testid="reset-password-error">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-password-submit-btn"
                className="w-full bg-[--accent] text-white py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
              >
                {busy ? "Saving…" : "Set new password & sign in"}
              </button>
              <div className="text-right">
                <Link to="/login" className="text-xs font-mono text-[--muted] hover:text-[--ink] underline underline-offset-4 decoration-dotted">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
